/**
 * The Archive — where deleted resources live (task 865).
 *
 * The shapes here are exactly what `GET /api/archive` and
 * `POST /api/archive/:id/restore` return (task 862), read in one place so the
 * app never forms a second opinion about what "deleted" means.
 *
 * **`id` is the LEDGER ENTRY id, not the resource id.** The deletion ledger is
 * the unit of both delete and restore: one row per User-initiated delete,
 * naming the resource and every row tombstoned alongside it in the same
 * transaction. That is what makes a restore exact rather than a guess at which
 * rows happened to share a timestamp — and it is why the restore route takes
 * this id and not the entity's.
 */

/** One resource that went with its parent in the same delete transaction. */
export interface ArchiveChild {
  entityType: string;
  entityId: string;
}

export interface ArchiveEntry {
  /** The ledger entry id — what `POST /archive/:id/restore` takes. */
  id: string;
  entityType: string;
  /** The deleted resource's own id. */
  entityId: string;
  displayName: string;
  children: ArchiveChild[];
  childCount: number;
  deletedAt: string;
  deletedBy: string | null;
}

/**
 * `409 NAME_IN_USE` — deleting frees the name, so a User may have built a new
 * `summarize` since. The live row is named so the User knows which one to
 * rename.
 */
export interface RestoreNameConflict {
  entityType: string;
  id: string;
  name: string;
}

/**
 * `409 DEPENDENCY_ARCHIVED` — the resource would come back pointing at
 * something that is itself still deleted. `deletionId` is that dependency's own
 * ledger entry, which is what makes "restore together" a two-call sequence the
 * page can drive rather than a dead end.
 */
export interface RestoreMissingDependency {
  entityType: string;
  id: string;
  name: string;
  deletionId: string;
}

/** The service's refusal codes on restore. */
export const NAME_IN_USE_CODE = 'NAME_IN_USE';
export const DEPENDENCY_ARCHIVED_CODE = 'DEPENDENCY_ARCHIVED';

/**
 * How an entity type reads in a sentence. The ledger stores the service's own
 * snake_case names; the Archive is a User-facing list and must not.
 */
export const ARCHIVE_ENTITY_LABELS: Record<string, string> = {
  endpoint: 'AI Service',
  agent: 'Agent',
  prompt: 'Prompt',
  collaboration_config: 'Collaboration config',
  mcp_connection: 'MCP connection',
  message_route: 'Message route',
  llm_config: 'Model config',
  knowledge_base: 'Knowledge base',
  knowledge_document: 'Knowledge document',
  artifact: 'Artifact',
  environment: 'Deployment environment',
  endpoint_test: 'Scenario',
  generated_endpoint_ui: 'Generated interface',
  generated_endpoint_ui_version: 'Interface version',
  agent_prompt: 'Prompt assignment',
  agent_mcp_connection: 'Connection attachment',
  agent_knowledge_base: 'Knowledge attachment',
  knowledge_base_document: 'Knowledge base membership',
};

/**
 * A readable label for an entity type, falling back to the stored name with its
 * underscores opened out — an unknown type reads as itself rather than as
 * nothing, so a new service-side entity is legible here before this map knows
 * about it.
 */
export function archiveEntityLabel(entityType: string): string {
  return ARCHIVE_ENTITY_LABELS[entityType] ?? entityType.replace(/_/g, ' ');
}
